/**
 * Permission checking utilities for frontend ACL
 */

/**
 * Check if current user has a specific permission
 * @param {Object} user - User object from AuthContext
 * @param {string} permission - Permission key to check
 * @returns {boolean} - True if user has permission
 */
export const hasPermission = (user, permission) => {
  if (!user) return false;
  
  // Super admin has all permissions
  if (user.role === 'super_admin') return true;
  
  // Check if permission exists in user's permissions array
  const userPermissions = user.permissions || [];
  return userPermissions.includes(permission);
};

/**
 * Check if user has ANY of the specified permissions
 * @param {Object} user - User object from AuthContext
 * @param {Array<string>} permissions - Array of permission keys
 * @returns {boolean} - True if user has at least one permission
 */
export const hasAnyPermission = (user, permissions) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  
  const userPermissions = user.permissions || [];
  return permissions.some(perm => userPermissions.includes(perm));
};

/**
 * Check if user has ALL of the specified permissions
 * @param {Object} user - User object from AuthContext
 * @param {Array<string>} permissions - Array of permission keys
 * @returns {boolean} - True if user has all permissions
 */
export const hasAllPermissions = (user, permissions) => {
  if (!user) return false;
  if (user.role === 'super_admin') return true;
  
  const userPermissions = user.permissions || [];
  return permissions.every(perm => userPermissions.includes(perm));
};

/**
 * Check if user is super admin
 * @param {Object} user - User object from AuthContext
 * @returns {boolean} - True if user is super admin
 */
export const isSuperAdmin = (user) => {
  return user && user.role === 'super_admin';
};

/**
 * Permission-based component wrapper
 * Only renders children if user has required permission
 */
export const PermissionGuard = ({ user, permission, children, fallback = null }) => {
  if (hasPermission(user, permission)) {
    return children;
  }
  return fallback;
};
